import { Controller } from "@hotwired/stimulus"
import { attachContactSuggest } from "../lib/contact_suggest"
import { track } from "../lib/mixpanel"

export default class extends Controller {
  static targets = ["modal", "modalBody", "companyContacts", "externalWrap", "externalList", "emptyState"]
  static values  = { membersUrl: String, updateUrl: String, external: Boolean,
                     suggestUrl: String, strings: Object, projectId: Number, phase: String }

  get _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  async openEdit() {
    this.modalTarget.classList.remove("hidden")
    this.modalBodyTarget.innerHTML = `
      <div class="flex items-center justify-center py-8">
        <svg class="animate-spin w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      </div>
    `

    try {
      const resp = await fetch(this.membersUrlValue, { headers: { "Accept": "application/json" } })
      const data = await resp.json()

      const companiesHtml = data.companies.map(co => `
        <div class="mb-4 last:mb-0" data-company-id="${co.company_id}">
          <p class="text-[13px] font-medium text-gray-800 mb-1">${this._escapeHtml(co.company_name)}
            <span class="text-[11px] font-normal text-gray-400 ml-1">${this._escapeHtml(co.role_name)}</span>
          </p>
          <div class="space-y-0.5">
            ${co.members.length === 0
              ? '<p class="text-[13px] text-gray-400 italic pl-1">No signed-up members</p>'
              : co.members.map(m => `
                <label class="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" value="${m.id}" ${m.selected ? "checked" : ""}
                         data-kc-company="${co.company_id}"
                         class="kc-checkbox rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                         data-action="change->key-contacts#toggleCheck">
                  <span class="text-[13px] text-gray-700">${this._escapeHtml(m.name)}</span>
                </label>
              `).join("")
            }
          </div>
        </div>
      `).join('<div class="border-t border-gray-100 my-3"></div>')

      const hasCompanies = data.companies.length > 0
      this.modalBodyTarget.innerHTML = companiesHtml + (this.externalValue ? this._externalSectionHtml(hasCompanies) : "")

      if (this.externalValue) {
        const existing = data.external_contacts || []
        if (existing.length) existing.forEach(c => this._appendExternalRow(c))
        else this._appendExternalRow()
      }

      this._enforceMaxPerCompany()
    } catch {
      this.modalBodyTarget.innerHTML = `<p class="text-[13px] text-red-500 py-4 text-center">Failed to load contacts.</p>`
    }
  }

  toggleCheck() { this._enforceMaxPerCompany() }

  _enforceMaxPerCompany() {
    const boxes = this.modalBodyTarget.querySelectorAll(".kc-checkbox")
    const byCompany = {}
    boxes.forEach(cb => {
      const cid = cb.dataset.kcCompany
      ;(byCompany[cid] ||= []).push(cb)
    })
    Object.values(byCompany).forEach(group => {
      const checked = group.filter(cb => cb.checked)
      group.forEach(cb => { cb.disabled = !cb.checked && checked.length >= 3 })
    })
  }

  // ── External contacts editor ──────────────────────────────────────
  _externalSectionHtml(hasCompanies) {
    return `
      ${hasCompanies ? '<div class="border-t border-gray-100 my-3"></div>' : ""}
      <div data-kc-external-section>
        <p class="text-[13px] font-medium text-gray-800">Other contacts</p>
        <p class="text-[11px] text-gray-400 mb-2">People not on the project team — owners, engineers, suppliers, haulers, inspectors…</p>
        <div data-kc-external-rows class="space-y-2"></div>
        <button type="button" data-action="key-contacts#addExternalRow"
                class="mt-2 inline-flex items-center gap-1 text-[13px] text-brand-600 hover:text-brand-700 font-medium cursor-pointer">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14m7-7H5"/></svg>
          Add contact
        </button>
      </div>`
  }

  addExternalRow() { this._appendExternalRow() }

  _appendExternalRow(c = {}) {
    const rows = this.modalBodyTarget.querySelector("[data-kc-external-rows]")
    if (!rows) return
    const div = document.createElement("div")
    div.className = "grid grid-cols-12 gap-1.5 items-center"
    div.setAttribute("data-kc-external-row", "")
    const inputCls = "px-2 py-1 text-[13px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
    div.innerHTML = `
      <input data-kc-ext="company" value="${this._attr(c.company)}" placeholder="Company" class="col-span-4 ${inputCls}">
      <input data-kc-ext="name" value="${this._attr(c.name)}" placeholder="Contact name" class="col-span-4 ${inputCls}">
      <input data-kc-ext="phone" value="${this._attr(c.phone)}" placeholder="Phone" class="col-span-3 ${inputCls}">
      <button type="button" data-action="key-contacts#removeExternalRow" title="Remove"
              class="col-span-1 flex justify-center text-gray-400 hover:text-red-500 cursor-pointer">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    rows.appendChild(div)
    this._attachSuggest(div)
  }

  // K7-34: typing a company or a name offers this company's contacts from
  // its other jobs (fills the row) and companies in ConsLog (name only).
  _attachSuggest(row) {
    if (!this.suggestUrlValue) return
    const set = (field, value) => { const el = row.querySelector(`[data-kc-ext="${field}"]`); if (el) el.value = value || "" }
    const onPick = (item, source) => {
      if (source === "other_jobs") {
        set("company", item.company); set("name", item.name); set("phone", item.phone)
      } else {
        set("company", item.name)
        row.querySelector('[data-kc-ext="name"]')?.focus()
      }
      track("Directory Suggestion Picked", { source, surface: "key_contacts", project_id: this.projectIdValue, project_phase: this.phaseValue })
    }
    const opts = { url: this.suggestUrlValue, strings: this.stringsValue, onPick }
    attachContactSuggest(row.querySelector('[data-kc-ext="company"]'), { ...opts, kind: "company" })
    attachContactSuggest(row.querySelector('[data-kc-ext="name"]'), { ...opts, kind: "name" })
  }

  removeExternalRow(e) { e.currentTarget.closest("[data-kc-external-row]")?.remove() }

  _gatherExternal() {
    const out = []
    this.modalBodyTarget.querySelectorAll("[data-kc-external-row]").forEach(r => {
      const company = r.querySelector('[data-kc-ext="company"]')?.value.trim() || ""
      const name = r.querySelector('[data-kc-ext="name"]')?.value.trim() || ""
      const phone = r.querySelector('[data-kc-ext="phone"]')?.value.trim() || ""
      if (company || name || phone) out.push({ company, name, phone })
    })
    return out
  }

  // ── Save ──────────────────────────────────────────────────────────
  async saveContacts() {
    const boxes = this.modalBodyTarget.querySelectorAll(".kc-checkbox")
    const byCompany = {}
    boxes.forEach(cb => {
      const cid = cb.dataset.kcCompany
      ;(byCompany[cid] ||= [])
      if (cb.checked) byCompany[cid].push(parseInt(cb.value))
    })
    const selections = Object.entries(byCompany).map(([cid, uids]) => ({ company_id: parseInt(cid), user_ids: uids }))
    const external_contacts = this.externalValue ? this._gatherExternal() : []

    try {
      const resp = await fetch(this.updateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken, "Accept": "application/json" },
        body: JSON.stringify({ selections, external_contacts })
      })
      const data = await resp.json()

      // Update team contacts per company
      this.companyContactsTargets.forEach(container => {
        const cid = container.dataset.companyId
        const contacts = data.contacts_by_company[cid]
        if (contacts !== undefined) {
          container.innerHTML = contacts.length === 0
            ? '<p class="text-[13px] text-gray-400 italic">No contacts selected</p>'
            : contacts.map(c => this._contactHtml(c)).join("")
        }
      })

      // Update external contacts list
      if (this.hasExternalListTarget) {
        const ext = data.external_contacts || []
        this.externalListTarget.innerHTML = ext.map(c => this._externalContactHtml(c)).join("")
        if (this.hasExternalWrapTarget) this.externalWrapTarget.classList.toggle("hidden", ext.length === 0)
        if (this.hasEmptyStateTarget) {
          const hasCompanies = this.companyContactsTargets.length > 0
          this.emptyStateTarget.classList.toggle("hidden", hasCompanies || ext.length > 0)
        }
      }
    } catch {
      // silent fail
    }

    this.hideModal()
  }

  hideModal(e) {
    if (e && e.target !== e.currentTarget) return
    this.modalTarget.classList.add("hidden")
  }
  stopPropagation(e) { e.stopPropagation() }

  _contactHtml(c) {
    const phone = c.phone ? `<span class="text-gray-400">${this._escapeHtml(c.phone)}</span>` : ""
    return `
      <div class="flex items-center justify-between gap-2 py-1 text-[13px]">
        <div class="flex-1 min-w-0">
          <span class="text-gray-700 font-medium">${this._escapeHtml(c.name)}</span>
          ${phone ? `<span class="text-gray-300 mx-1">&middot;</span>${phone}` : ""}
        </div>
        <a href="mailto:${this._escapeHtml(c.email)}" class="text-gray-400 hover:text-brand-600 shrink-0" title="Email ${this._escapeHtml(c.name)}">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"/>
          </svg>
        </a>
      </div>
    `
  }

  _externalContactHtml(c) {
    const name = (c.name || "").trim()
    const company = (c.company || "").trim()
    const phone = (c.phone || "").trim()
    const main = name || company || phone
    const rest = []
    if (company && company !== main) rest.push(company)
    if (phone && phone !== main) rest.push(phone)
    const restHtml = rest.map(r => `<span class="text-gray-300 mx-1">&middot;</span><span class="text-gray-400">${this._escapeHtml(r)}</span>`).join("")
    return `
      <div class="flex items-center justify-between gap-2 py-1 text-[13px]">
        <div class="flex-1 min-w-0">
          <span class="text-gray-700 font-medium">${this._escapeHtml(main)}</span>${restHtml}
        </div>
      </div>
    `
  }

  _attr(v) { return this._escapeHtml(v || "").replace(/"/g, "&quot;") }

  _escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
